// Zodiac reference content for the /zodiac/<sign> pages.
// Voice guide: no em dashes, no AI-slop cliches, concrete and honest.
// Lucky facts here mirror backend SIGN_FACTS for consistency across the site.

export const ZODIAC_SLUGS = [
  "aries", "taurus", "gemini", "cancer", "leo", "virgo",
  "libra", "scorpio", "sagittarius", "capricorn", "aquarius", "pisces",
];

export const ZODIAC = {
  aries: {
    name: "Aries",
    symbol: "\u2648",
    dates: "March 21 to April 19",
    element: "Fire",
    modality: "Cardinal",
    ruling_planet: "Mars",
    essence: "The first fire of the zodiac. Aries is the impulse to begin, to plant a flag, to be the one who tries the door.",
    strengths: [
      "Starts without waiting for permission.",
      "Reads a room and moves on it fast.",
      "Physical courage and a bias toward action.",
      "Loyal in a fight, and the fight does not have to be theirs.",
    ],
    shadows: [
      "Boredom hits fast. Follow-through is the lesson of a lifetime.",
      "Impatience mistaken for clarity.",
      "Prefers a fight to a conversation, sometimes.",
      "Reads slower people as obstacles instead of collaborators.",
    ],
    love: "Aries falls hard and out loud. There is nothing coy about them. In a relationship they need someone who can match their pace and hold their own when the fire runs hot. They lose respect for a partner who folds too easily and lose interest in one who cannot keep up. When the chemistry is right, they are the most present partner in the zodiac. When it is wrong, they leave without a long conversation about it.",
    career: "Aries does its best work at the beginning of things. Founding teams, launches, turnarounds, first-in-market plays. They wither in slow bureaucracies and outperform in roles that reward speed and nerve. Sales, entrepreneurship, emergency services, athletics, direct-action creative work. Give them a clear target and stay out of the way.",
    money: "Aries earns fast and spends faster. They are wired to bet, which makes them good at high-conviction moves and terrible at compounding boredom. Their financial mistake is not risk. It is impatience. A simple rule of thumb helps: nothing bought within 24 hours of first seeing it. Their money luck peaks in months when Mars runs strong.",
    famous_people: [
      "Lady Gaga (born March 28)",
      "Leonardo da Vinci (April 15)",
      "Robert Downey Jr. (April 4)",
      "Kourtney Kardashian (April 18)",
      "Vincent van Gogh (March 30)",
      "Emma Watson (April 15)",
    ],
    seo_title: "Aries Zodiac Sign: Traits, Love, Career, Compatibility",
    seo_desc: "The Aries sign, explored honestly. Traits, strengths and shadows, love and career patterns, lucky day, colours and numbers, plus which signs Aries actually gets along with.",
    lucky_day: "Tuesday",
    lucky_colors: ["Scarlet red", "Crimson", "Coral"],
    lucky_numbers: ["9", "1", "6"],
    lucky_gemstones: ["Bloodstone", "Carnelian", "Red Jasper"],
    lucky_direction: "East",
    allies: ["Leo", "Sagittarius", "Gemini"],
    mantra: "I move first, and I do not apologise for it.",
  },

  taurus: {
    name: "Taurus",
    symbol: "\u2649",
    dates: "April 20 to May 20",
    element: "Earth",
    modality: "Fixed",
    ruling_planet: "Venus",
    essence: "The steady earth of Venus. Taurus is the taste for what is beautiful, the memory of hunger, and a body that knows when a room is off.",
    strengths: [
      "Patient in a way that is almost tactical.",
      "A body-first intelligence. Reads texture, tone, comfort.",
      "Reliable. Says what they mean and does it.",
      "A talent for building something that lasts.",
    ],
    shadows: [
      "Stubbornness dressed up as principle.",
      "Comfort mistaken for happiness.",
      "Slow to leave things that have stopped serving them.",
      "Possessiveness in love and money, more than they admit.",
    ],
    love: "Taurus loves slow, steady and specific. They do not perform romance. They cook, they remember, they show up. They want a partner who values a shared home over a shared spotlight. When Taurus commits, they commit. When they leave, it is because something small broke a long time ago and neither of you was watching.",
    career: "Taurus thrives where craft compounds. Design, food, finance, real estate, farming, music, luxury goods, anything where taste and touch matter. They dislike environments that reward speed over quality. Their career arc looks slow for years and then very solid all at once.",
    money: "Taurus and money have a long, careful relationship. They save, they invest, they keep. Their trap is the opposite of Aries: they hold too long. A stock, a house, a job. Their money luck compounds most when they let go of one thing they were sentimental about.",
    famous_people: [
      "Adele (May 5)",
      "Dwayne Johnson (May 2)",
      "David Beckham (May 2)",
      "Audrey Hepburn (May 4)",
      "Sigmund Freud (May 6)",
      "Gigi Hadid (April 23)",
    ],
    seo_title: "Taurus Zodiac Sign: Traits, Love, Career, Compatibility",
    seo_desc: "The Taurus sign, no fluff. Traits, strengths and shadows, how Taurus loves and earns, lucky day, colours, gemstones and which signs actually match a Taurean pace.",
    lucky_day: "Friday",
    lucky_colors: ["Emerald green", "Pink", "Pale blue"],
    lucky_numbers: ["6", "4", "11"],
    lucky_gemstones: ["Emerald", "Rose Quartz", "Diamond"],
    lucky_direction: "South",
    allies: ["Virgo", "Capricorn", "Cancer"],
    mantra: "What is mine arrives without rush.",
  },

  gemini: {
    name: "Gemini",
    symbol: "\u264A",
    dates: "May 21 to June 20",
    element: "Air",
    modality: "Mutable",
    ruling_planet: "Mercury",
    essence: "The moving air of Mercury. Gemini is curiosity in motion, a mind that hears three conversations at once and enjoys all of them.",
    strengths: [
      "Verbal fluency. They can talk to anyone.",
      "Quick to see the pattern, quicker to see the flaw in it.",
      "Naturally curious in a way most people quietly envy.",
      "Adaptable without feeling betrayed by change.",
    ],
    shadows: [
      "Starts more than they finish.",
      "Uses charm the way other signs use silence, as a way not to be seen.",
      "Restless. Interprets calm as boredom.",
      "Can be evasive when the truth would slow them down.",
    ],
    love: "Gemini falls for a mind. A partner who reads, who argues well, who does not need every hour of the day accounted for. They struggle in relationships that mistake constant contact for closeness. What holds them is a partner who is genuinely interesting and slightly harder to read than they expected.",
    career: "Gemini belongs in language. Writing, journalism, teaching, negotiation, sales, media, translation, product marketing, comedy. They also do well in any role where two disciplines meet and someone has to translate between them. Their weakness is repetition. Route them to variety and they outperform.",
    money: "Gemini earns in ways that keep changing. Side projects, freelance, portfolio careers. Their money leaks in small charges and impulse subscriptions rather than big mistakes. A simple monthly review of standing debits does more for a Gemini's finances than any investment strategy.",
    famous_people: [
      "Angelina Jolie (June 4)",
      "Kanye West (June 8)",
      "Marilyn Monroe (June 1)",
      "Paul McCartney (June 18)",
      "Natalie Portman (June 9)",
      "Bob Dylan (May 24)",
    ],
    seo_title: "Gemini Zodiac Sign: Traits, Love, Career, Compatibility",
    seo_desc: "The Gemini sign, honestly. Traits, strengths and shadows, how Gemini loves and works, lucky day, colours, gemstones and which signs can actually keep up with a Gemini mind.",
    lucky_day: "Wednesday",
    lucky_colors: ["Yellow", "Light green", "Silver"],
    lucky_numbers: ["5", "3", "14"],
    lucky_gemstones: ["Pearl", "Agate", "Citrine"],
    lucky_direction: "West",
    allies: ["Libra", "Aquarius", "Aries"],
    mantra: "I learn out loud.",
  },

  cancer: {
    name: "Cancer",
    symbol: "\u264B",
    dates: "June 21 to July 22",
    element: "Water",
    modality: "Cardinal",
    ruling_planet: "Moon",
    essence: "The tide, ruled by the Moon. Cancer is the memory of home, the instinct to feed people, and an inner life larger than they usually let on.",
    strengths: [
      "Emotional intelligence that reads the room before it happens.",
      "Fiercely loyal to the small circle they choose.",
      "A talent for making a place feel like a home.",
      "Long memory, in the best sense. They remember what mattered to you.",
    ],
    shadows: [
      "Withdraws instead of speaking when hurt.",
      "Holds resentment past its expiry date.",
      "Confuses caretaking with control.",
      "Sensitive to slights that were not intended.",
    ],
    love: "Cancer loves like weather. It arrives in layers. They are slow to trust and, once they do, they are the most attentive partner in the zodiac. They need a partner who understands that a bad mood is not a threat and a quiet week is not the end. When Cancer feels safe, they are unshakeable.",
    career: "Cancer does its most durable work in fields that involve care, memory and place. Medicine, therapy, teaching, hospitality, real estate, food, family business. Their leadership style is unshowy but people follow them because they feel seen. Their trap is martyrdom. Set the boundary before it becomes a story.",
    money: "Cancer treats money as safety. They save with real discipline and are cautious with big moves. Their financial risk is emotional spending: buying comfort when what they actually need is a conversation. Their money luck peaks around family and property decisions made with a clear head.",
    famous_people: [
      "Princess Diana (July 1)",
      "Tom Cruise (July 3)",
      "Ariana Grande (June 26)",
      "Elon Musk (June 28)",
      "Selena Gomez (July 22)",
      "Meryl Streep (June 22)",
    ],
    seo_title: "Cancer Zodiac Sign: Traits, Love, Career, Compatibility",
    seo_desc: "The Cancer sign in plain language. Traits, strengths and shadows, love and career patterns, lucky day, colours, gemstones and the signs Cancer really matches.",
    lucky_day: "Monday",
    lucky_colors: ["Silver white", "Pearl", "Sea blue"],
    lucky_numbers: ["2", "7", "11"],
    lucky_gemstones: ["Pearl", "Moonstone", "Ruby"],
    lucky_direction: "North",
    allies: ["Scorpio", "Pisces", "Taurus"],
    mantra: "I belong to myself first.",
  },

  leo: {
    name: "Leo",
    symbol: "\u264C",
    dates: "July 23 to August 22",
    element: "Fire",
    modality: "Fixed",
    ruling_planet: "Sun",
    essence: "The fixed fire of the Sun. Leo is warmth turned into presence, the person who walks in and the room reorganises around them.",
    strengths: [
      "Natural warmth. Generous with attention and praise.",
      "A performer's discipline, even when the stage is a family dinner.",
      "Loyal, dramatic in the good way, unwilling to be small.",
      "Turns a project into a show, in the useful sense.",
    ],
    shadows: [
      "Ego wears them out faster than they admit.",
      "Needs the applause a little too much.",
      "Pride can stop them from apologising when it would fix everything.",
      "Confuses being the loudest with being right.",
    ],
    love: "Leo loves like a spotlight. Grand, warm, generous, and needing to be seen back. They struggle with partners who withhold affection as a strategy. Give a Leo real admiration, not flattery, and they will love you back with everything they have. Give them silence and they will find someone who claps.",
    career: "Leo belongs on a stage of some kind, even a small one. Leadership, performance, teaching, hospitality, brand, creative direction, politics. Their gift is turning ordinary work into something people want to gather around. Their trap is refusing feedback. When a Leo learns to hear a note, their career doubles.",
    money: "Leo earns with visibility and spends the same way. Their money story is generosity, mostly for others. Their financial mistake is refusing to look small enough to save. Their luck compounds fastest when a boring habit (automatic transfer, index fund, monthly review) is done privately and never mentioned.",
    famous_people: [
      "Barack Obama (August 4)",
      "Madonna (August 16)",
      "Meghan Markle (August 4)",
      "Chris Hemsworth (August 11)",
      "Jennifer Lopez (July 24)",
      "Coco Chanel (August 19)",
    ],
    seo_title: "Leo Zodiac Sign: Traits, Love, Career, Compatibility",
    seo_desc: "The Leo sign in full. Traits, strengths and shadows, how Leo loves and leads, lucky day, colours, gemstones and which signs actually match Leo's warmth.",
    lucky_day: "Sunday",
    lucky_colors: ["Gold", "Amber", "Royal orange"],
    lucky_numbers: ["1", "5", "9"],
    lucky_gemstones: ["Ruby", "Peridot", "Citrine"],
    lucky_direction: "East",
    allies: ["Aries", "Sagittarius", "Libra"],
    mantra: "I take up the room I was given.",
  },

  virgo: {
    name: "Virgo",
    symbol: "\u264D",
    dates: "August 23 to September 22",
    element: "Earth",
    modality: "Mutable",
    ruling_planet: "Mercury",
    essence: "Earth guided by Mercury. Virgo is precision as a form of care, the person who notices the crack before anyone else and quietly fixes it.",
    strengths: [
      "A working eye. Sees what is off in a system and can name it.",
      "Practical intelligence. Turns big ideas into checklists that ship.",
      "Reliable in a way that is often taken for granted.",
      "Kind in small, specific gestures rather than grand ones.",
    ],
    shadows: [
      "Self-criticism dressed up as high standards.",
      "Anxious perfectionism that stalls the finish.",
      "Can micromanage the people they love.",
      "Reads help as intrusion, even when they need it.",
    ],
    love: "Virgo loves in small, specific acts. The right cup of tea, the fixed cable, the calendar entry you forgot about. They are slow to say the big words and quick to show up when it counts. The partner they need is someone who reads acts of service as love and does not treat the small kindnesses as ordinary.",
    career: "Virgo builds careers on quality and detail. Editing, medicine, engineering, analysis, finance, wellness, education, research, hospitality operations. They dislike unclear briefs and thrive with real ownership over quality. Their growth edge is delegation without hovering.",
    money: "Virgo has the healthiest financial instincts of the zodiac and rarely trusts them enough. They over-save because a small worry never went away. Their money luck comes from small consistent moves and one properly delegated task per year that they were doing themselves for no good reason.",
    famous_people: [
      "Beyoncé (September 4)",
      "Michael Jackson (August 29)",
      "Zendaya (September 1)",
      "Freddie Mercury (September 5)",
      "Keanu Reeves (September 2)",
      "Mother Teresa (August 26)",
    ],
    seo_title: "Virgo Zodiac Sign: Traits, Love, Career, Compatibility",
    seo_desc: "The Virgo sign in plain language. Traits, strengths and shadows, love and career patterns, lucky day, colours, gemstones and the signs Virgo really matches.",
    lucky_day: "Wednesday",
    lucky_colors: ["Forest green", "Navy", "Beige"],
    lucky_numbers: ["5", "14", "23"],
    lucky_gemstones: ["Peridot", "Sapphire", "Carnelian"],
    lucky_direction: "West",
    allies: ["Taurus", "Capricorn", "Scorpio"],
    mantra: "Precision is a kind of love.",
  },

  libra: {
    name: "Libra",
    symbol: "\u264E",
    dates: "September 23 to October 22",
    element: "Air",
    modality: "Cardinal",
    ruling_planet: "Venus",
    essence: "Air moved by Venus. Libra is beauty as intelligence, the mind that sees both sides and refuses to pretend one of them does not exist.",
    strengths: [
      "A talent for balance in a room, a conversation, a decision.",
      "Diplomatic without being dishonest.",
      "Aesthetic instincts that make ordinary things feel intentional.",
      "Fair-minded, sometimes to their own cost.",
    ],
    shadows: [
      "Indecision when both sides have merit.",
      "Conflict avoidance that becomes its own conflict.",
      "Charming past the point of clarity.",
      "Outsources decisions to keep a room happy.",
    ],
    love: "Libra loves the idea of partnership more than most signs and takes the reality of it seriously. They want a partner they can plan with, dress with, argue with fairly. They dislike drama for its own sake but they will not run from a real disagreement. Their growth edge is asking for what they want in plain words instead of hoping to be read.",
    career: "Libra does well in any role where balance and taste are the deliverable. Law, diplomacy, design, mediation, HR, art direction, luxury, curation, therapy. They struggle in aggressive winner-takes-all cultures and thrive in teams that reward fairness and craft.",
    money: "Libra earns fine and spends better than most on things that make life feel considered. Their money leak is small: nice things, dinner out, gifts. Their real financial win is a joint or advised plan. Libra plus one trusted second opinion is a small money engine.",
    famous_people: [
      "Kim Kardashian (October 21)",
      "Serena Williams (September 26)",
      "John Lennon (October 9)",
      "Bruno Mars (October 8)",
      "Cardi B (October 11)",
      "Mahatma Gandhi (October 2)",
    ],
    seo_title: "Libra Zodiac Sign: Traits, Love, Career, Compatibility",
    seo_desc: "The Libra sign, honestly. Traits, strengths and shadows, how Libra loves and works, lucky day, colours, gemstones and the signs Libra actually matches.",
    lucky_day: "Friday",
    lucky_colors: ["Soft pink", "Light blue", "Lavender"],
    lucky_numbers: ["6", "15", "24"],
    lucky_gemstones: ["Opal", "Sapphire", "Rose Quartz"],
    lucky_direction: "South",
    allies: ["Gemini", "Aquarius", "Leo"],
    mantra: "Balance is a verb.",
  },

  scorpio: {
    name: "Scorpio",
    symbol: "\u264F",
    dates: "October 23 to November 21",
    element: "Water",
    modality: "Fixed",
    ruling_planet: "Mars (traditional), Pluto (modern)",
    essence: "Water with a spine. Scorpio is depth as a way of being, the sign that goes to the bottom of a feeling and comes back with something usable.",
    strengths: [
      "Emotional stamina. Can sit with what other signs walk away from.",
      "Perceptive to the point of unnerving.",
      "Loyal in a way that survives real damage.",
      "A talent for transformation, in themselves and in others.",
    ],
    shadows: [
      "Holds grudges the way other signs hold receipts.",
      "Control passed off as care.",
      "Suspicious when a straight answer would do.",
      "Punishes with silence, sometimes for a long time.",
    ],
    love: "Scorpio loves all the way in or not at all. They test partners in small ways to see who is really there. When a Scorpio trusts you, they trust you completely. When they leave, it is usually because they saw something long before you did. They need a partner who does not flinch from real conversation and does not lie about small things.",
    career: "Scorpio thrives where depth and trust matter. Psychology, medicine, investigative work, finance, research, strategy, therapy, intelligence, transformative business roles. They dislike shallow office politics and outperform in high-stakes environments that reward reading between the lines.",
    money: "Scorpio has strong money instincts and a tendency to concentrate risk. They win big when they trust their read and lose more than they should when they refuse to hedge. Their financial life improves the moment they pair conviction with a written rule they will not break.",
    famous_people: [
      "Julia Roberts (October 28)",
      "Bill Gates (October 28)",
      "Ryan Gosling (November 12)",
      "Katy Perry (October 25)",
      "Leonardo DiCaprio (November 11)",
      "Marie Curie (November 7)",
    ],
    seo_title: "Scorpio Zodiac Sign: Traits, Love, Career, Compatibility",
    seo_desc: "The Scorpio sign, uncensored. Traits, strengths and shadows, love and career patterns, lucky day, colours, gemstones and the signs Scorpio really matches.",
    lucky_day: "Tuesday",
    lucky_colors: ["Maroon", "Burgundy", "Black"],
    lucky_numbers: ["9", "4", "13"],
    lucky_gemstones: ["Topaz", "Citrine", "Obsidian"],
    lucky_direction: "North",
    allies: ["Cancer", "Pisces", "Virgo"],
    mantra: "I do not flinch from what is true.",
  },

  sagittarius: {
    name: "Sagittarius",
    symbol: "\u2650",
    dates: "November 22 to December 21",
    element: "Fire",
    modality: "Mutable",
    ruling_planet: "Jupiter",
    essence: "The wandering fire of Jupiter. Sagittarius is scale as an appetite, the sign that treats a horizon as an invitation.",
    strengths: [
      "Optimism grounded in real appetite for the world.",
      "Long view thinking, in career and in life.",
      "Honest to the point of relief.",
      "A gift for teaching what they have lived.",
    ],
    shadows: [
      "Blunt in ways that read as careless.",
      "Overpromises, then negotiates the ground later.",
      "Restless in relationships and jobs alike.",
      "Confuses freedom with running.",
    ],
    love: "Sagittarius loves someone who can travel with them, literally or otherwise. They are generous, funny, warm, and terrible at cages. The partner they need is someone who lives their own full life and welcomes them into it. Give a Sagittarius belief in the shared future and they will build the future with you. Give them a small life and they will politely leave.",
    career: "Sagittarius does its best work at scale. Publishing, teaching, travel, law, philosophy, sport, international business, media, religion, entrepreneurship. They dislike small careful roles that give them nothing to reach toward. Give them a wide brief and a real audience and they outperform.",
    money: "Sagittarius earns big and spends bigger, often on experiences. Their financial risk is scale: they think in years, not months. Their money luck compounds around teachers, mentors and formal education. A written monthly cap on the biggest lifestyle category (travel, food, experiences) does more for them than any budget tool.",
    famous_people: [
      "Taylor Swift (December 13)",
      "Brad Pitt (December 18)",
      "Miley Cyrus (November 23)",
      "Britney Spears (December 2)",
      "Walt Disney (December 5)",
      "Winston Churchill (November 30)",
    ],
    seo_title: "Sagittarius Zodiac Sign: Traits, Love, Career, Compatibility",
    seo_desc: "The Sagittarius sign in plain language. Traits, strengths and shadows, love and career patterns, lucky day, colours, gemstones and the signs Sagittarius really matches.",
    lucky_day: "Thursday",
    lucky_colors: ["Indigo", "Plum", "Burgundy"],
    lucky_numbers: ["3", "9", "12"],
    lucky_gemstones: ["Turquoise", "Yellow Topaz", "Lapis Lazuli"],
    lucky_direction: "East",
    allies: ["Aries", "Leo", "Aquarius"],
    mantra: "The road teaches what the chair cannot.",
  },

  capricorn: {
    name: "Capricorn",
    symbol: "\u2651",
    dates: "December 22 to January 19",
    element: "Earth",
    modality: "Cardinal",
    ruling_planet: "Saturn",
    essence: "Earth under Saturn. Capricorn is the long climb, the sign that treats time as an ally and understands that most real things take a decade.",
    strengths: [
      "Discipline without needing to be watched.",
      "A long horizon. Thinks in careers, not jobs.",
      "Quietly ambitious. Says less than they know.",
      "A dry, well-timed sense of humour when they trust you.",
    ],
    shadows: [
      "Works too hard at things that should be lighter.",
      "Confuses achievement with worth.",
      "Slow to trust, slow to let go.",
      "Old before their time in the wrong seasons.",
    ],
    love: "Capricorn loves seriously. They do not do casual well, even when they try. They want a partner who is building something with them, whether that is a business, a home, a family, or all of it. They struggle with partners who read their reserve as coldness. Their growth edge is play.",
    career: "Capricorn belongs where mastery pays off. Finance, law, medicine, executive leadership, engineering, government, high craft, real estate, structural roles in any industry. Their arc looks slow, then very solid, then, one decade in, quietly powerful. Their trap is refusing rest as a strategy.",
    money: "Capricorn is the strongest financial temperament in the zodiac. They earn steadily, save quietly, invest with discipline. Their trap is joyless money. Their financial life improves the day they start spending some of it on experiences and rest without treating it as a moral failure.",
    famous_people: [
      "Michelle Obama (January 17)",
      "Denzel Washington (December 28)",
      "Kate Middleton (January 9)",
      "Elvis Presley (January 8)",
      "Muhammad Ali (January 17)",
      "David Bowie (January 8)",
    ],
    seo_title: "Capricorn Zodiac Sign: Traits, Love, Career, Compatibility",
    seo_desc: "The Capricorn sign, told straight. Traits, strengths and shadows, love and career patterns, lucky day, colours, gemstones and the signs Capricorn really matches.",
    lucky_day: "Saturday",
    lucky_colors: ["Charcoal", "Brown", "Dark green"],
    lucky_numbers: ["8", "10", "26"],
    lucky_gemstones: ["Garnet", "Onyx", "Smoky Quartz"],
    lucky_direction: "South",
    allies: ["Taurus", "Virgo", "Pisces"],
    mantra: "I build slow on purpose.",
  },

  aquarius: {
    name: "Aquarius",
    symbol: "\u2652",
    dates: "January 20 to February 18",
    element: "Air",
    modality: "Fixed",
    ruling_planet: "Saturn (traditional), Uranus (modern)",
    essence: "The fixed air of the future. Aquarius is the mind that sees the system, the sign that arrives at a room slightly ahead of the room itself.",
    strengths: [
      "Original in a genuine way, not for effect.",
      "Reads systems, structures, and unspoken rules.",
      "Loyal to ideas and to the people who share them.",
      "Detached in the useful sense. Keeps a cool head under pressure.",
    ],
    shadows: [
      "Cold when they should be warm.",
      "Contrarian for its own sake.",
      "Emotionally distant when the moment asks for closeness.",
      "Prefers a big theory to a small feeling.",
    ],
    love: "Aquarius loves as a friend first. They want a partner who is also a collaborator, someone building something in the world with them. They are not fluent in the daily emotional register the way water signs are, but their loyalty is real and it lasts. The partner they need is someone who does not read cool as unkind.",
    career: "Aquarius belongs at the frontier. Technology, science, activism, design, systems work, education reform, media, engineering, futures. They struggle in traditional hierarchies and outperform in flat teams that reward original thought. Their edge is being an idea person who also ships.",
    money: "Aquarius has an unusual money temperament. They can be surprisingly generous and surprisingly detached, sometimes in the same week. Their financial risk is late attention. Their money luck arrives with automation. Set the system, forget the system, review it once a quarter.",
    famous_people: [
      "Oprah Winfrey (January 29)",
      "Ed Sheeran (February 17)",
      "Harry Styles (February 1)",
      "Shakira (February 2)",
      "Bob Marley (February 6)",
      "Charles Darwin (February 12)",
    ],
    seo_title: "Aquarius Zodiac Sign: Traits, Love, Career, Compatibility",
    seo_desc: "The Aquarius sign, without the cliches. Traits, strengths and shadows, love and career patterns, lucky day, colours, gemstones and the signs Aquarius really matches.",
    lucky_day: "Saturday",
    lucky_colors: ["Electric blue", "Silver", "Aqua"],
    lucky_numbers: ["4", "8", "22"],
    lucky_gemstones: ["Amethyst", "Garnet", "Aquamarine"],
    lucky_direction: "West",
    allies: ["Gemini", "Libra", "Sagittarius"],
    mantra: "The future is a room I am already in.",
  },

  pisces: {
    name: "Pisces",
    symbol: "\u2653",
    dates: "February 19 to March 20",
    element: "Water",
    modality: "Mutable",
    ruling_planet: "Jupiter (traditional), Neptune (modern)",
    essence: "The wide water of Neptune. Pisces is imagination as a first language, a soul with permeable edges who feels the room before they enter it.",
    strengths: [
      "Empathy that reads what has not been said yet.",
      "Creative in a natural, unforced way.",
      "Compassionate under real pressure, when it counts.",
      "A dreamer's persistence about the important things.",
    ],
    shadows: [
      "Absorbs other people's moods and calls it their own.",
      "Escapist tendencies. Screen, drink, drift.",
      "Slow to enforce a boundary, then resentful.",
      "Confuses fantasy with plan.",
    ],
    love: "Pisces loves with everything they have and needs the same in return. They are romantic in the classical sense: they want the sweep of it, not just the schedule. The partner they need is someone who takes their sensitivity seriously and gives them a container, gently. Pisces does not do well with cynics, and does very well with people who believe in something bigger than the two of them.",
    career: "Pisces belongs in creative and caring work. Art, film, music, writing, medicine, therapy, spiritual practice, teaching, philanthropy. They struggle in cynical corporate environments and outperform in fields where imagination is the deliverable. Their growth edge is finishing the work they started, not just starting it.",
    money: "Pisces and money have an ambivalent relationship. They are generous, sometimes to a fault, and they underprice their own work. Their money life improves with two habits: a clear rate they never break and a friend or advisor who reviews the numbers with them once a quarter without judgment.",
    famous_people: [
      "Rihanna (February 20)",
      "Steve Jobs (February 24)",
      "Justin Bieber (March 1)",
      "Albert Einstein (March 14)",
      "Bruce Willis (March 19)",
      "Trevor Noah (February 20)",
    ],
    seo_title: "Pisces Zodiac Sign: Traits, Love, Career, Compatibility",
    seo_desc: "The Pisces sign, told with care. Traits, strengths and shadows, love and career patterns, lucky day, colours, gemstones and the signs Pisces really matches.",
    lucky_day: "Thursday",
    lucky_colors: ["Sea green", "Lavender", "Aqua"],
    lucky_numbers: ["7", "3", "12"],
    lucky_gemstones: ["Aquamarine", "Amethyst", "Moonstone"],
    lucky_direction: "North",
    allies: ["Cancer", "Scorpio", "Capricorn"],
    mantra: "I feel it all and I keep going.",
  },
};
